/**
 * English (EN) UI Translations
 */
import type { UIStrings } from '../types'

export const en: UIStrings = {
  nav: {
    home: 'Home',
    about: 'About',
    education: 'Education',
    experience: 'Experience',
    projects: 'Projects',
    publications: 'Publications',
    skills: 'Skills',
    languages: 'Languages',
    certifications: 'Certifications',
    references: 'References',
    contact: 'Contact'
  },
  sections: {
    hero: 'Welcome',
    basics: 'About Me',
    profiles: 'Connect With Me',
    education: 'Education',
    languages: 'Languages',
    certifications: 'Workshops & Certifications',
    skills: 'Skills & Expertise',
    experiences: 'Professional Experience',
    projects: 'Projects & Portfolio',
    publications: 'Publications & Research',
    references: 'References',
    contact: 'Get In Touch'
  },
  sectionSubtitles: {
    education: 'My academic journey and continuous learning path',
    experiences: 'A timeline of roles, research, and contributions',
    projects: 'A collection of programming, bioinformatics, and research projects',
    references: 'Professional references available for verification. Contact details are provided upon request for legitimate professional inquiries.'
  },
  actions: {
    download_cv: 'Download CV',
    view_more: 'View More',
    view_less: 'View Less',
    contact_me: 'Contact Me',
    send_message: 'Send Message',
    book_call: 'Book a Call',
    copy: 'Copy',
    copied: 'Copied!',
    close: 'Close',
    expand: 'Expand',
    collapse: 'Collapse',
    filter: 'Filter',
    search: 'Search',
    clear: 'Clear',
    get_in_touch: 'Get in Touch',
    contact: 'Contact',
    learn_more: 'Learn More',
    read_more: 'Read more',
    show_less: 'Show less',
    show_phone: 'Show phone',
    hide_phone: 'Hide phone',
    email: 'Email',
    phone: 'Phone',
    expand_all: 'Expand All',
    collapse_all: 'Collapse All',
    back_to_top: 'Back to top',
    retry: 'Retry',
    open_pdf: 'Open PDF',
    theme: 'Theme',
    show_more: 'Show more',
    show_contact: 'Show Contact',
    hide_contact: 'Hide Contact',
    timeline: 'Timeline',
    by_category: 'By Category',
    has_link: 'Has Link'
  },
  forms: {
    name: 'Name',
    email: 'Email',
    subject: 'Subject',
    message: 'Message',
    send: 'Send',
    sending: 'Sending...',
    sent: 'Message sent!',
    error: 'An error occurred',
    success_message: 'Thank you for reaching out. I\'ll get back to you as soon as possible.',
    error_message: 'There was a problem sending your message. Please try again later or email directly.',
    contact_description: 'Send me a message and I\'ll get back to you as soon as possible',
    demo_notice: 'This is a demo form. To contact me, please use the email options below.',
    copy_email: 'Copy Email',
    email_copied: 'Email address copied to clipboard!',
    open_email_client: 'Open Email Client',
    or: 'or'
  },
  education: {
    institution: 'Institution',
    degree: 'Degree',
    field: 'Field of Study',
    location: 'Location',
    duration: 'Duration',
    gpa: 'GPA',
    present: 'Present'
  },
  experience: {
    role: 'Role',
    institution: 'Organization',
    duration: 'Duration',
    present: 'Present',
    responsibilities: 'Responsibilities',
    focus: 'Focus'
  },
  projects: {
    view_project: 'View Project',
    view_code: 'View Code',
    demo: 'Live Demo',
    no_projects: 'No projects found'
  },
  publications: {
    type: 'Type',
    status: 'Status',
    published: 'Published',
    submitted: 'Submitted',
    in_review: 'In Review',
    waiting: 'Waiting for Submission',
    authors: 'Authors',
    journal: 'Journal',
    year: 'Year',
    doi: 'DOI',
    isbn: 'ISBN'
  },
  skills: {
    proficiency: 'Proficiency',
    years: 'Years of Experience',
    category: 'Category',
    all_skills: 'All Skills',
    skills_across_domains: '{count} skills across {domains} domains',
    no_skills_matching: 'No skills found matching "{query}"',
    search_placeholder: 'Search skills...',
    more_categories: '+{count} more categories',
    skills_count: '{count} skills'
  },
  languages_section: {
    language: 'Language',
    proficiency: 'Proficiency',
    native: 'Native',
    fluent: 'Fluent',
    professional: 'Professional',
    intermediate: 'Intermediate',
    beginner: 'Beginner',
    studying: 'Studying',
    certification: 'Certification',
    description: 'Communication skills across multiple languages'
  },
  references: {
    available_on_request: 'References available upon request',
    position: 'Position',
    department: 'Department',
    institution: 'Institution',
    email: 'Email',
    phone: 'Phone',
    reference_letter: 'Reference Letter',
    privacy_notice: 'Note: Contact information is hidden by default to respect privacy. Click "Show Contact" to reveal details.'
  },
  footer: {
    copyright: '© {year} {name}. All rights reserved.',
    made_with: 'Made with',
    built_with: 'Built with React & TypeScript',
    konami_hint: 'Try the Konami code 🎮'
  },
  contact: {
    title: "Let's Connect",
    description: 'Whether you want to send a message or schedule a call, I\'m here to help',
    tab_message: 'Send Message',
    tab_booking: 'Book a Call'
  },
  errors: {
    something_went_wrong: 'Something went wrong',
    error_occurred: 'An error occurred while loading the application',
    error_details: 'Error details',
    try_again: 'Try again',
    runtime_error: 'This spark has encountered a runtime error',
    unexpected: 'Something unexpected happened while running the application. The error details are shown below. Contact the spark author and let them know about this issue.',
    error_details_label: 'Error Details:'
  },
  filters: {
    all: 'All',
    programming: 'Programming',
    bioinformatics: 'Bioinformatics',
    biotechnology: 'Biotechnology',
    academic: 'Academic',
    focus: 'Focus'
  },
  domains: {
    programming: 'Programming',
    bioinformatics: 'Bioinformatics',
    biotechnology: 'Biotechnology',
    academic: 'Academic',
    industry: 'Industry',
    robotics: 'Robotics',
    embedded_systems: 'Embedded Systems',
    student_projects: 'Student Projects'
  },
  empty: {
    no_data: 'No data available',
    no_results: 'No results found',
    no_education: 'No education entries',
    no_experience: 'No experience entries',
    no_projects: 'No projects',
    no_publications: 'No publications',
    no_skills: 'No skills listed',
    no_languages: 'No languages listed',
    no_certifications: 'No certifications',
    no_references: 'No references',
    no_profile_data: 'No profile data available',
    no_experiences_added: 'No professional experiences have been added yet.',
    no_experiences_filter: 'No experiences match the current focus filter. Try selecting "Full CV" to see all experiences.'
  },
  fallbacks: {
    untitled_role: 'Untitled Role',
    untitled_project: 'Untitled Project',
    untitled_publication: 'Untitled Publication',
    unknown_institution: 'Unknown Institution'
  },
  counts: {
    showing_x_of_y_projects: 'Showing {current} of {total} projects',
    showing_x_of_y_publications: 'Showing {current} of {total} publication{plural}'
  },
  toasts: {
    copied_to_clipboard: '{label} copied to clipboard',
    link_copied: '{network} link copied to clipboard'
  },
  a11y: {
    skip_to_content: 'Skip to main content',
    menu: 'Menu',
    close_menu: 'Close menu',
    select_language: 'Select language',
    select_theme: 'Select theme',
    select_focus: 'Select focus',
    open_modal: 'Open modal',
    close_modal: 'Close modal',
    required: 'required',
    scroll_to_top: 'Page corner - click to scroll to top',
    quick_actions: 'Quick actions',
    language_carousel: 'Language selector carousel',
    language_selection: 'Language selection',
    loading_image: 'Loading image',
    toggle_sidebar: 'Toggle Sidebar',
    pagination: 'pagination',
    previous_page: 'Go to previous page',
    next_page: 'Go to next page',
    breadcrumb: 'breadcrumb',
    filter_focus: 'Filter content focus',
    testimonial_nav: 'Testimonial navigation',
    media_viewer: 'Media viewer',
    previous_media: 'Previous media',
    next_media: 'Next media',
    scroll_to_about: 'Scroll to about section',
    certified: 'Certified',
    workshop: 'Workshop',
    search_certifications: 'Search certifications',
    search_skills: 'Search skills',
    clear_search: 'Clear search',
    list_view: 'List view',
    carousel_view: 'Carousel view',
    search_publications: 'Search publications',
    visit_profile: 'Visit {network} profile',
    page_scroll_progress: 'Page scroll progress',
    previous_skill_category: 'Previous skill category',
    next_skill_category: 'Next skill category',
    skill_category_nav: 'Skill category navigation',
    go_to_slide: 'Go to slide {number}',
    close_quick_actions: 'Close quick actions',
    open_quick_actions: 'Open quick actions',
    social_profiles: 'Social media profiles',
    previous_slide: 'Previous slide',
    next_slide: 'Next slide',
    close: 'Close'
  },
  media: {
    failed_to_load: 'Failed to load image',
    image_could_not_load: 'The image could not be loaded',
    click_to_open_pdf: 'Click below to open in a new tab',
    pdf_document: 'PDF Document'
  },
  profiles: {
    right_click_to_copy: 'Right-click to copy link',
    no_profiles: 'No profiles available'
  },
  booking: {
    title: 'Schedule a Call',
    description: 'Choose your preferred meeting type and select a time that works for you',
    meeting_type: 'Meeting Type',
    video_call: 'Video Call',
    phone_call: 'Phone Call',
    in_person: 'In Person',
    video_description: 'Meet via Zoom, Google Meet, or Microsoft Teams',
    phone_description: 'Direct phone conversation',
    in_person_description: 'Meet at a specified location',
    select_date: 'Select Date',
    select_time: 'Select Time',
    book_call: 'Book a Call',
    booking: 'Booking...',
    clear_selection: 'Clear Selection',
    your_selection: 'Your Selection:',
    success_title: 'Call booked!',
    success_description: 'Your {type} is scheduled for {date} at {time}.',
    error_title: 'Booking failed',
    error_description: 'There was a problem booking your call. Please try again later.',
    select_date_time: 'Please select a date and time',
    selection_cleared: 'Selection cleared',
    unavailable: 'unavailable',
    demo_notice: 'This is a demo booking interface. To schedule a real meeting, please use the link below.',
    schedule_external: 'Schedule via Calendly',
    calendly_cta: 'Open Scheduling Page',
    external_link_notice: 'You will be redirected to an external scheduling service.'
  },
  validation: {
    required: '{field} is required',
    invalid_email: 'Please enter a valid email address',
    min_length_2: '{field} must be at least 2 characters',
    min_length_3: '{field} must be at least 3 characters',
    min_length_10: '{field} must be at least 10 characters'
  },
  loading: {
    loading: 'Loading...',
    please_wait: 'Please wait'
  },
  pwa: {
    install_app: 'Install App',
    installed: 'App installed',
    offline_mode: 'You are offline',
    update_available: 'Update available',
    update_now: 'Update now'
  },
  download: {
    title: 'Download CV',
    description: 'Choose your preferred format and customize the content',
    format: 'Format',
    pdf_browser: 'PDF (Browser Print)',
    pdf_browser_desc: 'Use browser print dialog to save as PDF',
    json_export: 'JSON Export',
    json_export_desc: 'Raw data in JSON format',
    markdown: 'Markdown',
    markdown_desc: 'Plain text readable format',
    sections: 'Sections to Include',
    select_all: 'Select All',
    deselect_all: 'Deselect All',
    include_contact: 'Include Contact Info',
    include_references: 'Include References',
    preview: 'Preview',
    download: 'Download',
    downloading: 'Downloading...',
    success: 'Download complete',
    error: 'Download failed'
  },
  articles: {
    title: 'Articles & Blog',
    featured: 'Featured',
    read_time: '{minutes} min read',
    external_link: 'External article',
    no_articles: 'No articles available',
    filter_by_tag: 'Filter by tag',
    all_tags: 'All'
  },
  video: {
    play: 'Play video',
    pause: 'Pause video',
    mute: 'Mute',
    unmute: 'Unmute',
    fullscreen: 'Fullscreen',
    exit_fullscreen: 'Exit fullscreen',
    loading: 'Loading video...'
  },
  accessibility: {
    high_contrast: 'High contrast mode',
    font_size: 'Font size',
    increase_font: 'Increase font size',
    decrease_font: 'Decrease font size',
    reset_font: 'Reset font size',
    dyslexia_font: 'Dyslexia-friendly font',
    reduced_motion: 'Reduced motion'
  },
  caseStudy: {
    viewCaseStudy: 'View Case Study',
    collapse: 'Collapse',
    problem: 'Problem',
    solution: 'Solution',
    process: 'Process',
    results: 'Results',
    technologies: 'Technologies',
    gallery: 'Gallery',
    links: 'Links',
    metrics: 'Key Metrics',
    viewProject: 'View Project',
    viewCode: 'View Code',
    liveDemo: 'Live Demo',
    close: 'Close',
    of: 'of',
    previous: 'Previous image',
    next: 'Next image'
  },
  cookies: {
    title: 'Cookie Consent',
    message: 'We use cookies to analyze site traffic and improve your experience. You can opt out at any time.',
    accept: 'Accept',
    decline: 'Decline',
    dntMessage: 'We detected that you have Do Not Track enabled. Analytics tracking is disabled.',
    privacyPolicy: 'Privacy Policy',
    close: 'Close'
  },
  testimonials: {
    title: 'Testimonials & Recommendations',
    subtitle: 'What colleagues and clients say',
    linkedIn: 'View on LinkedIn',
    noTestimonials: 'No testimonials available',
    previous: 'Previous testimonial',
    next: 'Next testimonial',
    pause: 'Pause auto-rotation',
    play: 'Resume auto-rotation',
    of: 'of'
  },
  seo: {
    name: 'Ramin Yazdani',
    title: 'Biotechnologist & Developer',
    description: 'CV of Ramin Yazdani - Biotechnologist and Software Developer',
    separator: ' - '
  }
}
